import styled, { css, keyframes } from 'styled-components'
import type { BaseItem } from '@messenger/types'
import editorItem from 'styles/editorItem'
import {
  EDITOR_CASE_DETAIL_TABLE_BLOCK_CLASS_NAME,
  EDITOR_OVERFLOWING_ATTR,
  EDITOR_RUN_KIND_ATTR,
  EDITOR_SELECTION_HIGHLIGHT,
  EDITOR_TEXT_RUN_SELECTOR,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import {
  EDITOR_TEXT_EDITING_ATTR,
  EDITOR_TEXT_REVEALED_ATTR,
} from '@/utils/editor/textGeometryAttributes'
import zLayers from 'utils/styling/zLayers'

/**
 * The read-only paint is hidden until the box has settled once under loaded
 * faces, then fades in: the first paint a person sees is the fitted text in
 * its own face, never a fallback face that swaps out. Hidden text still lays
 * out, so the fit measures it as usual. The cap is the browser's own block
 * period, so a face that never answers cannot keep a box hidden. The fade
 * runs on the box itself, which outlives every editing session, so it plays
 * once per mount and not on every return from editing. The PDF surface
 * reveals with no fade: the worker captures the moment the box settles, and
 * a frame of a fade would print.
 */
const TEXT_REVEAL_CAP_MS = 3000
const TEXT_FADE_MS = 160

const revealAfterCap = keyframes`
  to { visibility: visible; }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

type EditorTextBoxProps = Partial<
  Pick<BaseItem, 'x' | 'y' | 'z' | 'width' | 'height' | 'edits'>
> & {
  full?: boolean
  print?: boolean
  environment?: string
}

export const EditorTextBox = styled.div<EditorTextBoxProps>(
  ({ environment }) => css`
    ${editorItem}
    background-color: transparent;
    &:not([${EDITOR_TEXT_REVEALED_ATTR}]):not([${EDITOR_TEXT_EDITING_ATTR}])
      > .editor-text-host {
      visibility: hidden;
      animation: ${revealAfterCap} 0s ${TEXT_REVEAL_CAP_MS}ms forwards;
    }
    ${environment !== 'pdf' &&
    css`
      &[${EDITOR_TEXT_REVEALED_ATTR}] {
        animation: ${fadeIn} ${TEXT_FADE_MS}ms ease-out;
      }
    `}
    ${environment === 'editor' &&
    css`
      /* A field run opens its modal on a double-click while editing, and so
         does every cell of a case-detail table; the pointer says so, where
         the text around them shows the I-beam. */
      &[${EDITOR_TEXT_EDITING_ATTR}]
        ${EDITOR_TEXT_RUN_SELECTOR}[${EDITOR_RUN_KIND_ATTR}='field'],
      &[${EDITOR_TEXT_EDITING_ATTR}]
        .${EDITOR_CASE_DETAIL_TABLE_BLOCK_CLASS_NAME}
        td {
        cursor: pointer;
      }
      &:has([${EDITOR_OVERFLOWING_ATTR}='true'])::after {
        content: 'Text doesn’t fit. Resize the box, or pick a smaller font size.';
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 2px;
        z-index: 9;
        padding: 3px 8px;
        background: #c92a2a;
        color: #ffffff;
        font-size: 10px;
        font-family: sans-serif;
        line-height: 1.4;
        border-radius: 3px;
        white-space: normal;
        max-width: 200px;
        pointer-events: none;
      }
    `}
  `,
)

export const SelectionHoldLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`

export const SelectionHoldRect = styled.div`
  position: absolute;
  background: ${EDITOR_SELECTION_HIGHLIGHT};
`

const protectPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(204, 0, 0, 0); background: rgba(204, 0, 0, 0); }
  30% { box-shadow: 0 0 0 2px rgba(204, 0, 0, 0.55); background: rgba(204, 0, 0, 0.16); }
  100% { box-shadow: 0 0 0 0 rgba(204, 0, 0, 0); background: rgba(204, 0, 0, 0); }
`

export const ProtectPulseLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
`

export const ProtectPulseRect = styled.div`
  position: absolute;
  border-radius: 2px;
  animation: ${protectPulse} 0.5s ease;
`

export const EmptyHint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;
  color: #000000;
  font-family: sans-serif;
  font-size: 14px;
  line-height: 1;
  pointer-events: none;
  user-select: none;
`

export const InkDebugLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
`

export const InkClusterOutline = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 1px solid rgba(255, 0, 0, 0.9);
  pointer-events: none;
`

/**
 * The rings' canvas over the box: its user units are the box's own layout
 * px, the units the mount measures the rings in, and it lets a ring's pad
 * paint past its edge.
 */
export const FieldOutlineLayer = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  z-index: 2;
`

/**
 * One ring: a dashed 2 px stroke along a path padded out from the group's
 * line boxes, no fill, so the text under it stays untouched. Where a
 * wrapped run's lines meet, the stroke runs along their shared edge.
 */
export const FieldOutline = styled.path`
  fill: none;
  stroke: rgba(42, 138, 205, 0.75);
  stroke-width: 2;
  stroke-dasharray: 6 3;
  stroke-linejoin: round;
`

export const FieldTooltip = styled.div`
  position: fixed;
  z-index: ${zLayers.topLayer};
  transform: translate(-50%, calc(-100% - 6px));
  width: max-content;
  max-width: 240px;
  padding: 4px 8px;
  background: #1f2937;
  color: #ffffff;
  font-family: sans-serif;
  font-size: 10px;
  line-height: 1.4;
  text-align: left;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  user-select: none;
`
